import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';

export class SharedValidators {
  // Combined Validator for required and no empty spaces
  static requiredAndNoEmptySpaces(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    let returnValue = null;

    if (typeof value === 'string') {
      if (value.trim().length === 0) {
        returnValue = {
          requiredAndNoEmptySpaces:
            "This field is required and can't be only spaces.",
        };
      } else if (value.trim().length !== value.length) {
        returnValue = {
          requiredAndNoEmptySpaces: 'Remove leading or trailing spaces.',
        };
      }
    } else if (value === null || value === undefined || value === '') {
      returnValue = {
        requiredAndNoEmptySpaces: "This field is required and can't be empty.",
      };
    }

    return returnValue;
  }

  static singleSpaceOrHyphen(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;

    if (typeof value !== 'string' || value.length === 0) {
      return null;
    }

    // Reject consecutive spaces or consecutive hyphens
    if (/\s{2,}|-{2,}/.test(value)) {
      return {
        singleSpaceOrHyphen: 'Only one consecutive space or hyphen is allowed.',
      };
    }

    return null;
  }

  static eitherLengthCheckValidator(length1: any, length2: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value.toString();
      if (value.trim().length === 0) {
        return {
          eitherLengthCheckValidator:
            "This field is required and can't be only spaces.",
        };
      } else if (value.trim().length !== value.length) {
        return {
          eitherLengthCheckValidator: 'Remove leading or trailing spaces.',
        };
      } else if (value.length !== length1 && value.length !== length2) {
        return {
          eitherLengthCheckValidator: `Value must be exactly ${length1} or ${length2} digits long.`,
        };
      }

      return null;
    };
  }

  static lengthCheckValidator(
    length: number,
    options: {
      onlyNumbers?: boolean;
      noSpecialChars?: boolean;
      required?: boolean;
    } = {}
  ) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.toString() ?? '';
      const trimmedValue = value.trim();

      if (options.required && trimmedValue.length === 0) {
        return {
          lengthCheckValidator:
            "This field is required and can't be only spaces.",
        };
      }

      // Skip validation for optional fields
      if (!options.required && trimmedValue.length === 0) {
        return null;
      }

      if (/\s/.test(value)) {
        return {
          lengthCheckValidator: 'Spaces are not allowed.',
        };
      }

      if (value.length !== length) {
        return {
          lengthCheckValidator: `Value must be exactly ${length} characters long.`,
        };
      }

      if (options.onlyNumbers && !/^\d+$/.test(value)) {
        return {
          lengthCheckValidator: 'Only numbers are allowed.',
        };
      }

      if (options.noSpecialChars && !/^[a-zA-Z0-9]*$/.test(value)) {
        return {
          lengthCheckValidator: 'Special characters are not allowed.',
        };
      }

      return null;
    };
  }

  // Stricter email pattern to allow only lowercase letters in the domain
  static validEmail(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    // Enforces lowercase letters for the domain part
    const pattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    return value && !pattern.test(value)
      ? { validEmail: 'Please enter a valid Email ID.' }
      : null;
  }

  // Validator for minimum length
  static minLengthRequired(min: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      return value === '' || (value && value.length < min)
        ? { minLengthRequired: `Minimum ${min} characters required.` }
        : null;
    };
  }

  // Validator for maximum length
  static maxLengthRequired(max: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return value && value.length > max
        ? { maxLengthRequired: `Maximum ${max} characters allowed.` }
        : null;
    };
  }

  // Validator for length range
  static rangeLength(min: any, max: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return value && (value.length < min || value.length > max)
        ? {
            rangeLength: `Length must be between ${min} and ${max} characters.`,
          }
        : null;
    };
  }

  // Validator for exact length
  static exactLengthValidator(requiredLength: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      // Allow empty values to pass validation
      if (!value) return null;
      const actualLength = value.length;
      return actualLength !== requiredLength
        ? {
            exactLength: `Value must be exactly ${requiredLength} characters long received length ${actualLength}.`,
          }
        : null;
    };
  }

  /**
   * Validates that the given field is a valid Date.
   */
  static isValidDate(control: AbstractControl): ValidationErrors | null {
    if (!control.value || isNaN(new Date(control.value).getTime())) {
      return { isValidDate: 'Invalid date format.' };
    }
    return null;
  }

  /**
   * Validates that the start date is not greater than the end date.
   * @param startDateKey - The form control name for the start date.
   * @param endDateKey - The form control name for the end date.
   */
  static dateRangeValidator(startDateKey: any, endDateKey: any) {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const startDate = formGroup.get(startDateKey)?.value;
      const endDate = formGroup.get(endDateKey)?.value;

      if (!startDate || !endDate) {
        return null; // Skip validation if either field is empty
      }

      if (new Date(startDate) > new Date(endDate)) {
        return { invalidDateRange: 'Start date cannot be after end date.' };
      }

      return null;
    };
  }

  static mobileNumberValidation(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === '') {
      return { mobileNumberValidation: 'Mobile number is required.' };
    }

    // Regex pattern for mobile numbers with optional country code and validation
    const pattern = /^(?:\+?91|0)?[7-9][0-9]{9}$/;

    // Check if value is not null/empty and does not match the pattern
    return value && !pattern.test(value)
      ? { mobileNumberValidation: 'Enter a valid mobile number.' }
      : null;
  }

  // Function to mark all form controls as touched
  static markAllAsTouched(formGroupOrArray: FormGroup | FormArray) {
    Object.keys(formGroupOrArray.controls).forEach((field) => {
      const control = formGroupOrArray.get(field);

      // If it's a FormGroup, recursively mark its children
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  // Function to check and find invalid controls
  static findInvalidControls(formGroup: FormGroup, formName: any) {
    const invalidFields = [];
    const controls = formGroup.controls;

    for (const name in controls) {
      if (controls[name].invalid) {
        invalidFields.push(
          `[${formName}] Field: ${name}, Errors: ${JSON.stringify(controls[name].errors)}`
        );
      }
    }

    console.info(`[${formName}] Invalid Fields:`, invalidFields);
  }

  // Function to list all controls
  static findControls(formGroup: FormGroup, formName: any) {
    const allFields = Object.keys(formGroup.controls);
    console.info(`[${formName}] All Fields:`, allFields);
  }

  // Function to list all controls along with values and validators
  static findControlsAndValue(formGroup: FormGroup, formName: any) {
    console.info(`\n[${formName}] Form Controls Details:`);

    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.controls[controlName];
      const value = control.value;

      console.info(`Control  - ${controlName}: Value ${value}`);
    });
  }

  // Validator for non-numeric and non-special characters
  static nonNumericNoSpecialChars(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    const pattern = /^[a-zA-Z ]*$/; // Allows alphabets and spaces
    return value && !pattern.test(value)
      ? { nonNumericNoSpecialChars: 'Only alphabets and spaces are allowed.' }
      : null;
  }

  static alphaNumericOnly(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const pattern = /^[a-zA-Z0-9]*$/; // Allows only letters and numbers
    return value && !pattern.test(value)
      ? { alphaNumericOnly: 'Special characters and spaces are not allowed' }
      : null;
  }

  static noSpecialCharacters(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    const pattern = /^[a-zA-Z0-9 ]*$/; // Allows only letters and numbers with spaces
    return value && !pattern.test(value)
      ? { noSpecialCharacters: 'Special characters are not allowed' }
      : null;
  }

  static onlyNumbersAllowed(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const pattern = /^[0-9]*$/; // Allows only digits
    return value && !pattern.test(value)
      ? { onlyNumbersAllowed: 'Only numbers are allowed.' }
      : null;
  }

  // Combined validator for non-numeric, no special characters, and length
  static nonNumericWithLengthValidation(min: any, max: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const pattern = /^[a-zA-Z ]*$/;
      if (
        value &&
        (!pattern.test(value) || value.length < min || value.length > max)
      ) {
        return {
          nonNumericWithLengthValidation: `Only alphabets and spaces allowed with a length between ${min} and ${max} characters.`,
        };
      }
      return null;
    };
  }

  // Validator for numeric values between 1 and 65535 (port number validation)
  static portValidation(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const pattern =
      /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;
    return value && !pattern.test(value)
      ? { portValidation: 'Enter a valid port number (1-65535).' }
      : null;
  }

  // Validator for IP addresses
  static ipValidation(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const pattern =
      /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/;
    return value && !pattern.test(value)
      ? { ipValidation: 'Enter a valid IP address.' }
      : null;
  }

  /**
   * Validator for alphanumeric characters with configurable allowed special characters
   * @param allowedSpecialChars A string containing the allowed special characters
   * @returns A validator function
   */
  static alphanumericWithCustomSpecialChars(allowedSpecialChars: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const escapedChars = allowedSpecialChars.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      ); // Escape special regex characters
      const pattern = new RegExp(`^[a-zA-Z0-9${escapedChars}]*$`); // Construct dynamic regex
      return value && !pattern.test(value)
        ? {
            alphanumericWithCustomSpecialChars: `Only alphanumeric characters and the following special characters are allowed: ${allowedSpecialChars}`,
          }
        : null;
    };
  }

  /**
   * Validator for alphanumeric characters with configurable allowed special characters,
   * and optional minimum and maximum length constraints.
   * @param allowedSpecialChars A string containing the allowed special characters.
   * @param minLength Minimum length of the input (inclusive).
   * @param maxLength Maximum length of the input (inclusive).
   * @returns A validator function.
   */
  static alphanumericWithCustomSpecialCharsAndLengthValidation(
    allowedSpecialChars: any,
    minLength = 0,
    maxLength = Infinity
  ) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null; // No validation for empty values

      const escapedChars = allowedSpecialChars.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      ); // Escape special regex characters
      const pattern = new RegExp(`^[a-zA-Z0-9${escapedChars}]*$`); // Construct dynamic regex

      const errors: ValidationErrors = {};
      if (!pattern.test(value)) {
        errors['alphanumericWithCustomSpecialCharsAndLengthValidation'] =
          `Only alphanumeric characters and the following special characters are allowed: ${JSON.stringify(
            allowedSpecialChars.split('')
          )}`;
      }
      if (value.length < minLength) {
        errors['alphanumericWithCustomSpecialCharsAndLengthValidation'] =
          `Minimum length is ${minLength} characters.`;
      }
      if (value.length > maxLength) {
        errors['alphanumericWithCustomSpecialCharsAndLengthValidation'] =
          `Maximum length is ${maxLength} characters.`;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }
}
